// De Videotheek: één zoekbalk over eigen video's én inspiratie.
//
// Leeg = bladeren door onze eigen opgeleverde video's; typen zoekt op gesproken
// tekst, beeld, klant of projectnummer; een geplakte Frame.io-link of een
// projectnummer wordt herkend en direct opgezocht. Beheer (uploaden, lijst,
// verwerking) zit achter één knop — het is er voor wie het nodig heeft, niet
// voor iedereen die een film zoekt (videotheek-plan stap 5, 12 sep 2026).
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, TextField, InputAdornment, Box, Card, CardContent, CardMedia, Grid, Chip,
  CircularProgress, Alert, Button, LinearProgress, IconButton, Paper, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MovieIcon from '@mui/icons-material/Movie';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LinkIcon from '@mui/icons-material/Link';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { leesJson, meldFout } from '../../utils/apiFout';
import { VIDEO_CATEGORIEEN, toonCategorie } from '../../config/videoCategorieen';
import PageHeader from '../../ui-components/PageHeader';
import VideoDetailDialog from './VideoDetailDialog';
import VideoManagement from './VideoManagement';
import VideoUpload from './VideoUpload';
import { useEigenThumb } from './eigenThumb';

const API_BASE = `${AI_ENGINE_URL}/api/v1/video-search`;
const PAGINA = 48;

const BRON = {
  own: { bg: '#e3f2fd', color: '#1565c0', label: 'Eigen' },
  inspiration: { bg: '#fce4ec', color: '#c62828', label: 'Inspiratie' },
};

// Een Frame.io-link of een projectnummer is geen zoekterm: die zoeken we exact op.
const isLinkOfNummer = (q) => /frame\.io|f\.io\/|^https?:\/\//i.test(q) || /^\d{4,5}$/.test(q.trim());

const formatDuur = (seconds) => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Eigen video's uit /videos en /resolve in dezelfde vorm als /unified.
const alsTegel = (v) => ({
  id: v.id, source: 'own', title: v.filename, client_name: v.client_name,
  project_number: v.project_number, thumbnail_url: v.thumb_small, thumb_maken: v.thumb_maken, duration: v.duration,
  category: v.category, player_url: v.player_url, snippet: null,
});

function VideoTegel({ video, onOpen }) {
  const bron = BRON[video.source] || BRON.own;
  const eigen = video.source !== 'inspiration';
  const thumb = useEigenThumb(eigen ? video.id : null, video.thumbnail_url, eigen && video.thumb_maken);
  return (
    <Card onClick={() => onOpen(video)} sx={{
      height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer',
      transition: 'transform 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
    }}>
      <Box sx={{ position: 'relative', bgcolor: '#111', aspectRatio: '16 / 9' }}>
        {thumb && (
          <CardMedia component="img" image={thumb} alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.15)', opacity: 0, transition: 'opacity 0.2s', '&:hover': { opacity: 1 },
        }}>
          <PlayArrowIcon sx={{ fontSize: 48, color: 'white' }} />
        </Box>
        {video.source === 'inspiration' && (
          <Chip label={bron.label} size="small" sx={{
            position: 'absolute', top: 8, left: 8, bgcolor: bron.bg, color: bron.color,
            fontSize: '0.6rem', height: 20, fontWeight: 600,
          }} />
        )}
        {video.duration ? (
          <Chip label={formatDuur(video.duration)} size="small" sx={{
            position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '0.65rem',
          }} />
        ) : null}
      </Box>
      <CardContent sx={{ flexGrow: 1, py: 1.25, px: 1.75, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap title={video.title}>
          {video.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {[video.client_name, video.project_number ? `#${video.project_number}` : null,
            toonCategorie(video.category) ? video.category : null].filter(Boolean).join(' · ') || ' '}
        </Typography>
        {video.snippet && (
          <Typography variant="caption" color="text.secondary" sx={{
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            lineHeight: 1.4, mt: 0.5,
          }}>
            {video.snippet}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

const VideoSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [modus, setModus] = useState('bladeren');       // bladeren | zoeken | link
  const [gevondenAls, setGevondenAls] = useState(null); // resolve: 'frameio', 'projectnummer', …
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categorie, setCategorie] = useState(null);
  const [bron, setBron] = useState(null);               // null | own | inspiration
  const [categorieTelling, setCategorieTelling] = useState({});
  const zoekveld = useRef(null);
  const debounceRef = useRef(null);

  // Detailvenster met de lijst erbij, zodat ←/→ bladert.
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLijst, setDetailLijst] = useState([]);
  const detailIndex = detailLijst.indexOf(selectedVideoId);

  // Beheer achter één knop.
  const [beheerAnchor, setBeheerAnchor] = useState(null);
  const [paneel, setPaneel] = useState(null);           // null | beheer | upload | verwerking
  const [stats, setStats] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [startingProcess, setStartingProcess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch(`${API_BASE}/categories`);
        const data = await leesJson(resp, 'Categorieën laden');
        setCategorieTelling(data.counts || {});
      } catch (e) { meldFout(e, 'Categorieën laden'); }
    })();
  }, []);

  // `/` zet de cursor in het zoekveld, zoals in de Beeldbank.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (e.key === '/' && tag !== 'input' && tag !== 'textarea' && !e.target?.isContentEditable) {
        e.preventDefault(); zoekveld.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const laad = useCallback(async (q, { pagina: p = 1, cat = categorie, src = bron } = {}) => {
    const term = (q || '').trim();
    setLoading(true); setError(null);
    try {
      if (!term) {
        // Bladeren: onze eigen opgeleverde video's, per pagina.
        const params = new URLSearchParams({ status: 'completed', page: String(p), page_size: String(PAGINA) });
        if (cat) params.set('category', cat);
        const data = await leesJson(await authFetch(`${API_BASE}/videos?${params}`), 'Video\'s laden');
        const tegels = (data.videos || []).map(alsTegel);
        setResults((oud) => (p === 1 ? tegels : [...oud, ...tegels]));
        setTotal(data.total_filtered ?? tegels.length);
        setModus('bladeren'); setGevondenAls(null);
      } else if (isLinkOfNummer(term)) {
        const data = await leesJson(await authFetch(`${API_BASE}/resolve?q=${encodeURIComponent(term)}&limit=60`), 'Opzoeken');
        const tegels = (data.matches || []).map(alsTegel);
        setResults(tegels); setTotal(tegels.length);
        setModus('link'); setGevondenAls(data.matched_as || null);
      } else {
        const params = new URLSearchParams({ q: term, limit: '60' });
        if (cat) params.set('category', cat);
        if (src) params.set('source_filter', src);
        const data = await leesJson(await authFetch(`${API_BASE}/unified?${params}`), 'Zoeken');
        setResults(data.results || []); setTotal((data.results || []).length);
        setModus('zoeken'); setGevondenAls(null);
      }
      setPagina(p);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [categorie, bron]);

  // Eén laadmoment: typen (kort uitgesteld), een filterklik, de eerste keer openen.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { laad(query); }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, laad]);

  const openVideo = (video) => {
    if (video.source === 'inspiration') { navigate(`/inspiration?video=${video.id}`); return; }
    setSelectedVideoId(video.id);
    setDetailLijst(results.filter((r) => r.source === 'own').map((r) => r.id));
    setDetailOpen(true);
  };

  // ── verwerking (beheer) ──
  const fetchStats = useCallback(async () => {
    try { setStats(await leesJson(await authFetch(`${API_BASE}/stats`), 'Statistieken laden')); }
    catch (e) { meldFout(e, 'Statistieken laden'); }
  }, []);
  useEffect(() => { if (paneel === 'verwerking') fetchStats(); }, [paneel, fetchStats]);
  useEffect(() => {
    if (!processingStatus?.running) return undefined;
    const interval = setInterval(async () => {
      try {
        const data = await leesJson(await authFetch(`${API_BASE}/process/status`), 'Verwerkingsstatus laden');
        setProcessingStatus(data);
        if (!data.running) { clearInterval(interval); fetchStats(); }
      } catch (e) { console.warn('Verwerkingsstatus laden mislukt (poll blijft draaien):', e.message); }
    }, 5000);
    return () => clearInterval(interval);
  }, [processingStatus?.running, fetchStats]);

  const startProcessing = async (limit = 10) => {
    // Bulk-verwerking kost AI-tegoed en is niet te stoppen: eerst bevestigen (behalve de 5-test)
    if (limit >= 50) {
      const ok = await bevestig({
        titel: 'Verwerking starten?',
        tekst: `${limit >= 5000 ? 'Alle' : `Maximaal ${limit}`} video's worden geanalyseerd met AI. Dit kost geld en is niet te stoppen.`,
        knop: 'Starten',
      });
      if (!ok) return;
    }
    setStartingProcess(true);
    try {
      await leesJson(await authFetch(`${API_BASE}/process/start?limit=${limit}`, { method: 'POST' }), 'Verwerking starten');
      setProcessingStatus({ running: true, progress: 'Starting...', processed: 0, total: limit });
    } catch (e) {
      setError('Processing starten mislukt: ' + e.message);
    } finally { setStartingProcess(false); }
  };

  const zoekt = modus !== 'bladeren';
  const gevondenTekst = gevondenAls === 'frameio' || (gevondenAls || '').includes('link') ? 'de Frame.io-link'
    : gevondenAls === 'projectnummer' ? 'het projectnummer' : gevondenAls;

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="video_library"
        title="Videotheek"
        subtitle="Alle opgeleverde video's en inspiratie. Zoek op wat er gezegd wordt, wat je ziet, klant of projectnummer — of plak een Frame.io-link."
        accent={{ color: '#635983', bg: '#dbcdff' }}
        rightActions={(
          <>
            <Button size="small" variant="outlined" color="inherit" startIcon={<SettingsIcon />}
              onClick={(e) => setBeheerAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>
              Beheer
            </Button>
            <Menu anchorEl={beheerAnchor} open={Boolean(beheerAnchor)} onClose={() => setBeheerAnchor(null)}>
              <MenuItem onClick={() => { setBeheerAnchor(null); setPaneel('upload'); }}>
                <CloudUploadIcon fontSize="small" sx={{ mr: 1.5 }} /> Video uploaden
              </MenuItem>
              <MenuItem onClick={() => { setBeheerAnchor(null); setPaneel('beheer'); }}>
                <ListAltIcon fontSize="small" sx={{ mr: 1.5 }} /> Video's beheren
              </MenuItem>
              <MenuItem onClick={() => { setBeheerAnchor(null); setPaneel('verwerking'); }}>
                <RocketLaunchIcon fontSize="small" sx={{ mr: 1.5 }} /> Verwerking en kosten
              </MenuItem>
            </Menu>
          </>
        )}
      />

      <TextField
        fullWidth inputRef={zoekveld} autoFocus
        placeholder="Zoek op inhoud ('windmolens', 'Capgemini', 'bouwen bouwen bouwen'), of plak een Frame.io-link of projectnummer…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && query) { setQuery(''); } }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
          endAdornment: (
            <InputAdornment position="end">
              {loading ? <CircularProgress size={20} /> : query ? (
                <IconButton size="small" onClick={() => setQuery('')} aria-label="Wissen"><CloseIcon fontSize="small" /></IconButton>
              ) : <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>/</Typography>}
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
      />

      {/* Bron alleen bij zoeken: bladeren gaat over onze eigen video's; inspiratie heeft zijn eigen scherm. */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {modus === 'zoeken' && (
          <>
            {[[null, 'Alles'], ['own', 'Eigen video\'s'], ['inspiration', 'Inspiratie']].map(([w, naam]) => (
              <Chip key={naam} label={naam} size="small"
                variant={bron === w ? 'filled' : 'outlined'} color={bron === w ? 'primary' : 'default'}
                onClick={() => setBron(w)} />
            ))}
            <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.5 }} />
          </>
        )}
        {VIDEO_CATEGORIEEN.filter((c) => categorieTelling[c] || categorie === c).map((c) => (
          <Chip key={c} size="small"
            label={categorieTelling[c] ? `${c} · ${categorieTelling[c]}` : c}
            variant={categorie === c ? 'filled' : 'outlined'} color={categorie === c ? 'primary' : 'default'}
            onClick={() => setCategorie((h) => (h === c ? null : c))} sx={{ fontSize: '0.72rem' }} />
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {loading ? '' : zoekt ? `${results.length} treffers` : `${results.length}${results.length < total ? ` van ${total}` : ''} video's`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {modus === 'link' && !loading && (
        <Alert severity={results.length ? 'success' : 'info'} icon={<LinkIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          {results.length
            ? `Herkend als ${gevondenTekst || 'link of nummer'}: ${results.length} video${results.length === 1 ? '' : "'s"} gevonden in onze eigen Frame.io.`
            : `Herkend als ${gevondenTekst || 'link of nummer'}, maar daar staat niets van in de Videotheek. Is de video nog niet gescand? Uploaden kan via Beheer.`}
        </Alert>
      )}

      {!loading && results.length === 0 && modus === 'zoeken' && (
        <Alert severity="info">Niets gevonden voor "{query}"{categorie ? ` in ${categorie}` : ''}. Probeer een ander woord, of haal het categoriefilter weg.</Alert>
      )}
      {!loading && results.length === 0 && modus === 'bladeren' && (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <MovieIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>Nog geen geanalyseerde video's{categorie ? ` in ${categorie}` : ''}</Typography>
          <Typography variant="body2" color="text.secondary">Via Beheer → Verwerking laat je de eerste video's analyseren.</Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        {results.map((video) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={`${video.source}-${video.id}`}>
            <VideoTegel video={video} onOpen={openVideo} />
          </Grid>
        ))}
      </Grid>

      {modus === 'bladeren' && !loading && results.length < total && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" onClick={() => laad('', { pagina: pagina + 1 })} sx={{ textTransform: 'none' }}>
            Meer laden ({total - results.length})
          </Button>
        </Box>
      )}

      <VideoDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        videoId={selectedVideoId}
        zoekterm={modus === 'zoeken' ? query : ''}
        onVorige={detailIndex > 0 ? () => setSelectedVideoId(detailLijst[detailIndex - 1]) : undefined}
        onVolgende={detailIndex >= 0 && detailIndex < detailLijst.length - 1 ? () => setSelectedVideoId(detailLijst[detailIndex + 1]) : undefined}
        positie={detailIndex >= 0 ? { nr: detailIndex + 1, van: detailLijst.length } : null}
      />

      {/* Beheer-panelen */}
      <Dialog open={paneel === 'upload'} onClose={() => setPaneel(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Video uploaden</DialogTitle>
        <DialogContent><VideoUpload /></DialogContent>
      </Dialog>
      <Dialog open={paneel === 'beheer'} onClose={() => setPaneel(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Video's beheren</DialogTitle>
        <DialogContent><VideoManagement /></DialogContent>
      </Dialog>
      <Dialog open={paneel === 'verwerking'} onClose={() => setPaneel(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Verwerking en kosten</DialogTitle>
        <DialogContent>
          {stats && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[[stats.completed, 'Geanalyseerd'], [stats.total_frameio_videos, 'Totaal video\'s'],
                [stats.with_transcript, 'Met transcript'], [`$${stats.total_cost_usd?.toFixed(2)}`, 'API-kosten']].map(([w, naam]) => (
                <Grid item xs={6} key={naam}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>{w}</Typography>
                    <Typography variant="caption" color="text.secondary">{naam}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
          {processingStatus?.running && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{processingStatus.progress}</Typography>
              <LinearProgress variant="determinate" sx={{ mb: 0.5 }}
                value={processingStatus.total > 0 ? ((processingStatus.processed + (processingStatus.failed || 0)) / processingStatus.total) * 100 : 0} />
              <Typography variant="caption" color="text.secondary">
                {processingStatus.processed} verwerkt, {processingStatus.failed || 0} mislukt, ${processingStatus.cost_usd?.toFixed(3)} kosten
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pb: 1 }}>
            <Button variant="outlined" onClick={() => startProcessing(5)} disabled={processingStatus?.running || startingProcess}
              startIcon={startingProcess ? <CircularProgress size={16} /> : null} sx={{ textTransform: 'none' }}>
              Test 5 video's
            </Button>
            <Button variant="outlined" onClick={() => startProcessing(50)} disabled={processingStatus?.running || startingProcess} sx={{ textTransform: 'none' }}>
              Verwerk 50 video's
            </Button>
            <Button variant="contained" onClick={() => startProcessing(5000)} disabled={processingStatus?.running || startingProcess} sx={{ textTransform: 'none' }}>
              Verwerk alle video's
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VideoSearch;
